import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Bookmark, Plus, X, Phone, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { LoansSection } from '@/components/apartados/LoansSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  active: { label: 'Activo', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  paid: { label: 'Liquidado', cls: 'bg-emerald-500 text-white' },
  cancelled: { label: 'Cancelado', cls: 'bg-slate-300 text-slate-700' },
};

export const Apartados = () => {
  const { refreshProducts } = useDataCache();
  const [section, setSection] = useState('goods');
  const [apartados, setApartados] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState({ open: false, apt: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, apt: null, amount: '', method: 'efectivo', deliver_now: false, notes: '', submitting: false });

  const fetchApartados = async () => {
    try {
      const { data } = await axios.get(`${API}/apartados`, { withCredentials: true });
      setApartados(data);
    } catch (e) {
      toast.error('Error al cargar apartados');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchApartados(); }, []);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    let list = apartados;
    if (statusFilter !== 'all') list = list.filter(a => a.status === statusFilter);
    const t = searchTerm.toLowerCase().trim();
    if (t) list = list.filter(a =>
      (a.folio || '').toLowerCase().includes(t) ||
      (a.client_name || '').toLowerCase().includes(t) ||
      (a.client_phone || '').toLowerCase().includes(t)
    );
    return list;
  }, [apartados, statusFilter, searchTerm]);

  const openPayment = (apt, deliverDefault = false) => setPaymentDialog({
    open: true, apt, amount: '', method: 'efectivo',
    deliver_now: deliverDefault || apt.delivered ? false : false, notes: '', submitting: false
  });

  const submitPayment = async () => {
    const amt = parseFloat(paymentDialog.amount);
    if (!amt || amt <= 0) { toast.error('Monto inválido'); return; }
    setPaymentDialog(s => ({ ...s, submitting: true }));
    try {
      const payload = { amount: amt, method: paymentDialog.method, deliver_now: paymentDialog.deliver_now, notes: paymentDialog.notes.trim() || null };
      const { data } = await axios.post(`${API}/apartados/${paymentDialog.apt.id}/payments`, payload, { withCredentials: true });
      if (data.status === 'paid') {
        toast.success(`✅ Apartado ${data.folio} liquidado · venta creada`);
        refreshProducts();
      } else {
        toast.success(`Pago de $${amt.toFixed(2)} registrado · resta $${data.remaining_amount.toFixed(2)}`);
      }
      setPaymentDialog({ open: false, apt: null, amount: '', method: 'efectivo', deliver_now: false, notes: '', submitting: false });
      if (detail.open && detail.apt?.id === data.id) setDetail({ open: true, apt: data });
      fetchApartados();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      setPaymentDialog(s => ({ ...s, submitting: false }));
    }
  };

  const markDelivered = async (apt) => {
    if (!window.confirm(`¿Marcar como entregado el apartado ${apt.folio}?`)) return;
    try {
      await axios.post(`${API}/apartados/${apt.id}/deliver`, {}, { withCredentials: true });
      toast.success('Marcado como entregado');
      fetchApartados();
      if (detail.open && detail.apt?.id === apt.id) {
        const { data } = await axios.get(`${API}/apartados/${apt.id}`, { withCredentials: true });
        setDetail({ open: true, apt: data });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const cancelApt = async (apt) => {
    if (!window.confirm(`¿Cancelar apartado ${apt.folio}? ${apt.delivered ? '(producto YA entregado, stock NO se devuelve)' : 'Se devolverá el stock.'}`)) return;
    try {
      await axios.delete(`${API}/apartados/${apt.id}`, { withCredentials: true });
      toast.success('Cancelado');
      fetchApartados();
      refreshProducts();
      if (detail.open && detail.apt?.id === apt.id) setDetail({ open: false, apt: null });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const totals = useMemo(() => {
    const active = apartados.filter(a => a.status === 'active');
    return {
      activeCount: active.length,
      activeCash: active.reduce((s, a) => s + (a.paid_amount || 0), 0),
      activePending: active.reduce((s, a) => s + (a.remaining_amount || 0), 0),
    };
  }, [apartados]);

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Bookmark size={32} weight="bold" className="text-violet-600" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">Apartados</h1>
              <p className="text-muted-foreground">Layaway: enganche + abonos hasta liquidar</p>
            </div>
          </div>
        </div>

        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-4">
          {[['goods', '🔖 Mercancía'], ['loans', '💸 Dinero prestado']].map(([id, lbl]) => (
            <button key={id} onClick={() => setSection(id)} data-testid={`apartados-section-${id}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${section === id ? 'bg-white shadow text-violet-700' : 'text-slate-500'}`}>{lbl}</button>
          ))}
        </div>
        {section === 'loans' ? <LoansSection /> : (<>
        {/* Contador del cajón de apartados */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-violet-600 text-white rounded-sm p-4" data-testid="apartados-cash-box">
            <p className="text-xs uppercase tracking-wide opacity-80">💰 Cajón de apartados</p>
            <p className="text-3xl font-bold mt-1">${totals.activeCash.toFixed(2)}</p>
            <p className="text-[11px] opacity-70 mt-1">Acumulado en abonos · NO entra al corte hasta liquidar</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Apartados activos</p>
            <p className="text-3xl font-bold mt-1">{totals.activeCount}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Folios sin liquidar</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Saldo pendiente</p>
            <p className="text-3xl font-bold mt-1 text-rose-600">${totals.activePending.toFixed(2)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Por cobrar a clientes</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {['active', 'paid', 'cancelled', 'all'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded text-sm font-medium btn-transition ${statusFilter === s ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
              data-testid={`filter-${s}`}>
              {s === 'all' ? 'Todos' : (STATUS_LABEL[s]?.label || s)}
            </button>
          ))}
          <Input className="ml-auto max-w-xs" placeholder="Buscar folio/cliente/teléfono…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-input" />
        </div>

        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Folio</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Cliente</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Vendedor</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Vence</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Total</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Pagado</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Saldo</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase">Estatus</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase">Entrega</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && <tr><td colSpan="10" className="px-3 py-8 text-center text-muted-foreground">Cargando…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan="10" className="px-3 py-8 text-center text-muted-foreground">Sin apartados</td></tr>
                )}
                {filtered.map((a) => {
                  const expired = a.status === 'active' && a.due_date < today;
                  const st = STATUS_LABEL[a.status] || STATUS_LABEL.active;
                  return (
                    <tr key={a.id} className={`hover:bg-slate-50 ${expired ? 'bg-rose-50' : ''}`} data-testid={`apartado-row-${a.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{a.folio}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{a.client_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={10} /> {a.client_phone}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{a.seller_name}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1"><Calendar size={11} /> {a.due_date}</div>
                        {expired && <span className="text-[10px] text-rose-700 font-semibold">VENCIDO</span>}
                      </td>
                      <td className="px-3 py-2 text-right">${a.total_amount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">${a.paid_amount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-semibold">${a.remaining_amount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {a.delivered ? <span className="text-emerald-700 font-semibold">✓</span> : <span className="text-slate-400">pendiente</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setDetail({ open: true, apt: a })} className="text-xs px-2 py-1 border rounded hover:bg-slate-100" data-testid={`view-${a.id}`}>Ver</button>
                          {a.status === 'active' && (
                            <button onClick={() => openPayment(a)} className="text-xs px-2 py-1 rounded bg-violet-600 text-white hover:bg-violet-700" data-testid={`pay-${a.id}`}>+Abono</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </div>

      {/* Detalle */}
      <Dialog open={detail.open} onOpenChange={(o) => !o && setDetail({ open: false, apt: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 {detail.apt?.folio} — {detail.apt?.client_name}</DialogTitle>
          </DialogHeader>
          {detail.apt && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 p-3 rounded">
                <div><span className="text-muted-foreground">Teléfono:</span> {detail.apt.client_phone}</div>
                <div><span className="text-muted-foreground">Vendedor:</span> {detail.apt.seller_name}</div>
                <div><span className="text-muted-foreground">Vence:</span> {detail.apt.due_date}</div>
                <div><span className="text-muted-foreground">Creado:</span> {new Date(detail.apt.created_at).toLocaleString('es-MX')}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Entrega:</span> {detail.apt.delivered ? <span className="text-emerald-700 font-bold">✓ Entregado {detail.apt.delivered_at ? `(${new Date(detail.apt.delivered_at).toLocaleString('es-MX')})` : ''}</span> : <span className="text-amber-700 font-semibold">pendiente</span>}</div>
                {detail.apt.notes && <div className="col-span-2"><span className="text-muted-foreground">Notas:</span> {detail.apt.notes}</div>}
              </div>
              <div>
                <p className="font-semibold mb-1">Productos</p>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-2 py-1 text-left">Producto</th>
                      <th className="px-2 py-1 text-right">Cant</th>
                      <th className="px-2 py-1 text-right">Precio</th>
                      <th className="px-2 py-1 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.apt.items.map((it, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-2 py-1">{it.product_name}</td>
                        <td className="px-2 py-1 text-right">{it.quantity}</td>
                        <td className="px-2 py-1 text-right">${it.unit_price.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">${(it.quantity * it.unit_price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="font-bold">
                    <tr><td colSpan="3" className="px-2 py-1 text-right">Total:</td><td className="px-2 py-1 text-right">${detail.apt.total_amount.toFixed(2)}</td></tr>
                  </tfoot>
                </table>
              </div>
              <div>
                <p className="font-semibold mb-1">Pagos ({detail.apt.payments?.length || 0})</p>
                <div className="space-y-1">
                  {(detail.apt.payments || []).map(p => (
                    <div key={p.id} className="flex justify-between items-center text-xs px-2 py-1 border rounded">
                      <div>
                        <span className="font-mono">${p.amount.toFixed(2)}</span>
                        <span className="ml-2 text-muted-foreground">{p.method}</span>
                        {p.notes && <span className="ml-2 text-slate-500">— {p.notes}</span>}
                      </div>
                      <span className="text-muted-foreground">{new Date(p.date).toLocaleString('es-MX')}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-sm font-semibold">
                  <span>Pagado: <span className="text-emerald-700">${detail.apt.paid_amount.toFixed(2)}</span></span>
                  <span>Resta: <span className="text-rose-700">${detail.apt.remaining_amount.toFixed(2)}</span></span>
                </div>
              </div>
              {detail.apt.status === 'active' && (
                <div className="flex gap-2 border-t pt-3">
                  <Button onClick={() => openPayment(detail.apt)} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="detail-add-payment">
                    <Plus size={16} className="mr-1" /> Abono
                  </Button>
                  {!detail.apt.delivered && (
                    <Button variant="outline" onClick={() => markDelivered(detail.apt)} data-testid="detail-deliver">
                      Marcar entregado
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => cancelApt(detail.apt)} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="detail-cancel">
                    <X size={16} className="mr-1" /> Cancelar
                  </Button>
                </div>
              )}
              {detail.apt.status === 'paid' && detail.apt.sale_id && (
                <div className="bg-emerald-50 border border-emerald-300 rounded p-2 text-sm">
                  ✅ Liquidado el {new Date(detail.apt.liquidated_at).toLocaleString('es-MX')} · Venta generada
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de abono */}
      <Dialog open={paymentDialog.open} onOpenChange={(o) => !o && !paymentDialog.submitting && setPaymentDialog({ open: false, apt: null, amount: '', method: 'efectivo', deliver_now: false, notes: '', submitting: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>+ Abono · {paymentDialog.apt?.folio}</DialogTitle>
          </DialogHeader>
          {paymentDialog.apt && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-2 rounded text-sm">
                <div>Saldo actual: <span className="font-bold">${paymentDialog.apt.remaining_amount.toFixed(2)}</span></div>
                <div className="text-xs text-muted-foreground">Cliente: {paymentDialog.apt.client_name}</div>
              </div>
              <div>
                <Label>Monto del abono *</Label>
                <Input type="number" step="0.01" value={paymentDialog.amount} onChange={(e) => setPaymentDialog(s => ({ ...s, amount: e.target.value }))} autoFocus data-testid="payment-amount" />
                <button type="button" onClick={() => setPaymentDialog(s => ({ ...s, amount: paymentDialog.apt.remaining_amount.toFixed(2) }))} className="text-xs text-violet-600 hover:underline mt-1">
                  Liquidar (${paymentDialog.apt.remaining_amount.toFixed(2)})
                </button>
              </div>
              <div>
                <Label>Método *</Label>
                <Select value={paymentDialog.method} onValueChange={(v) => setPaymentDialog(s => ({ ...s, method: v }))}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="mixto">Mixto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!paymentDialog.apt.delivered && (
                <label className="flex items-center gap-2 cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded">
                  <input type="checkbox" checked={paymentDialog.deliver_now} onChange={(e) => setPaymentDialog(s => ({ ...s, deliver_now: e.target.checked }))} data-testid="payment-deliver-now" />
                  <span className="text-sm">¿El cliente se lleva el producto ahora?</span>
                </label>
              )}
              <div>
                <Label>Notas (opcional)</Label>
                <Input value={paymentDialog.notes} onChange={(e) => setPaymentDialog(s => ({ ...s, notes: e.target.value }))} data-testid="payment-notes" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setPaymentDialog({ open: false, apt: null, amount: '', method: 'efectivo', deliver_now: false, notes: '', submitting: false })} disabled={paymentDialog.submitting}>Cancelar</Button>
                <Button onClick={submitPayment} disabled={paymentDialog.submitting} className="bg-violet-600 hover:bg-violet-700" data-testid="payment-submit">
                  {paymentDialog.submitting ? 'Guardando…' : 'Registrar abono'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Apartados;
